import { Badge, Button, Checkbox, Switch, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { ErrorState, Section } from "@/components/ui"
import { errorMessage, apiFetch } from "@/lib/sdk"
import { useQueryClient } from "@tanstack/react-query"
import { currentSubscription, disablePush, enablePush, pushSupport, useAlertSettings, useSaveAlertPrefs, useSendTest, type AlertPriority, type TestResult } from "@/lib/staff-alerts"
import { useCurrentMe } from "@/lib/permissions"
import { SettingsTitle } from "./settings-general"

/**
 * Settings → Your notifications (backend lib/staff-alerts.ts): this person's alerts. Turn push on
 * for this device, see and remove their other devices, send a real test to their own devices only,
 * choose push / email per priority and mute individual events. Critical (security) alerts can't
 * be switched off.
 */

const PRIORITY: Record<AlertPriority, { title: string; hint: string }> = {
  critical: { title: "Critical: security and sensitive activity", hint: "Always sent by push, in the bell and by email. Can't be turned off." },
  high: { title: "High: orders, refunds, products, stock, staff", hint: "Push and the bell. Email if you turn it on." },
  medium: { title: "Medium: general store activity", hint: "The bell. Push and email if you turn them on." },
}

export function StaffAlertsSettingsPage() {
  const me = useCurrentMe()
  const { data, error, isLoading } = useAlertSettings()
  const save = useSaveAlertPrefs()
  const test = useSendTest()
  const qc = useQueryClient()
  const [here, setHere] = useState<"loading" | "on" | "off">("loading")
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<TestResult | null>(null)
  const support = pushSupport()

  useEffect(() => {
    currentSubscription()
      .then((s) => setHere(s ? "on" : "off"))
      .catch(() => setHere("off"))
  }, [data?.devices.length])

  if (error) return <ErrorState error={error} />
  if (isLoading || !data) return <Text size="small">Loading…</Text>
  const p = data.prefs
  const set = (patch: Parameters<typeof save.mutate>[0]) => save.mutate(patch, { onError: (e) => toast.error(errorMessage(e)) })
  const refresh = () => qc.invalidateQueries({ queryKey: ["staff-alerts", "settings"] })

  const toggleHere = async () => {
    setBusy(true)
    try {
      if (here === "on") {
        await disablePush()
        setHere("off")
        toast.success("Notifications turned off on this device")
      } else {
        const r = await enablePush(data.public_key)
        if (r === "on") {
          setHere("on")
          toast.success("Notifications are on for this device")
        } else if (r === "denied") toast.error("Your browser blocked notifications for this site. Allow them in the browser's site settings (the icon next to the address), then try again.")
        else toast.error("This browser can't receive notifications.")
      }
      refresh()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const sendTest = (email: boolean) =>
    test.mutate(
      { email },
      {
        onSuccess: (r) => {
          setResult(r)
          const ok = r.push.filter((x) => x.ok).length
          if (r.push_status === "no_device") toast.warning("No device has notifications turned on yet. Turn them on above first; the test is in your bell.")
          else if (ok) toast.success(`Test sent to ${ok} device${ok === 1 ? "" : "s"}${email ? " and your email" : ""}`)
          else toast.error("The test didn't reach any device. See the details below.")
          refresh()
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )

  const removeDevice = async (id: string) => {
    try {
      await apiFetch(`/admin/terify/me/alerts/devices/${id}`, { method: "DELETE" })
      refresh()
      currentSubscription().then((s) => setHere(s ? "on" : "off"))
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  const groups = (["critical", "high", "medium"] as AlertPriority[]).map((pr) => ({ pr, events: data.events.filter((e) => e.priority === pr) }))
  const isAdmin = data.role === "owner" || data.role === "admin"

  return (
    <div className="flex flex-col gap-4">
      <SettingsTitle title="Your notifications" subtitle={`Alerts for ${me.user.email}: in the bell, as push notifications on your devices, and by email.`} />

      <Section title="Get notifications">
        <div className="flex items-start justify-between gap-4 px-4 pb-4">
          <div>
            <Text size="small" weight="plus">Staff notifications</Text>
            <Text size="xsmall" className="text-ui-fg-subtle">
              {isAdmin ? "On for the owner and admins. Turn it off to stop everything except security alerts in the bell." : "Off for staff unless turned on. Turn it on to get the alerts below."}
            </Text>
          </div>
          <Switch checked={p.enabled} onCheckedChange={(v) => set({ enabled: v })} />
        </div>
      </Section>

      <Section title="This device">
        <div className="flex flex-col gap-3 px-4 pb-4">
          {support === "ios_needs_install" ? (
            <Text size="small">
              On iPhone and iPad, notifications work from the Home Screen app: tap Share in Safari, choose <em>Add to Home Screen</em>, open Keel from there, sign in and come back to this page.
            </Text>
          ) : support !== "ok" ? (
            <Text size="small">This browser can't receive push notifications{support === "insecure" ? " (the page isn't on https)" : ""}. Use Chrome, Edge, Firefox or Safari.</Text>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <Text size="small" weight="plus">Push notifications on this {/iPhone|Android|iPad/.test(navigator.userAgent) ? "phone" : "computer"}</Text>
                <Text size="xsmall" className="text-ui-fg-subtle">
                  {here === "on" ? "On. Alerts pop up here even when Keel isn't open." : Notification.permission === "denied" ? "Blocked in this browser: allow notifications for this site in the browser's site settings, then turn on." : "Off."}
                </Text>
              </div>
              <Button size="small" variant={here === "on" ? "secondary" : "primary"} onClick={toggleHere} isLoading={busy || here === "loading"}>
                {here === "on" ? "Turn off" : "Turn on"}
              </Button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 border-t border-ui-border-base pt-3">
            <Button size="small" variant="secondary" onClick={() => sendTest(false)} isLoading={test.isPending}>
              Send test notification
            </Button>
            <Button size="small" variant="transparent" onClick={() => sendTest(true)} disabled={test.isPending}>
              Send test + email
            </Button>
            <Text size="xsmall" className="text-ui-fg-subtle">Goes only to you ({me.user.email}): every device below, your bell{" "}and, with "+ email", your inbox.</Text>
          </div>
          {result ? (
            <div className="rounded-lg bg-ui-bg-subtle p-3 text-[12px]">
              {result.push.length ? (
                <ul>
                  {result.push.map((d) => (
                    <li key={d.device_id}>
                      {d.ok ? "✓" : "✗"} {d.label ?? "Device"}: {d.ok ? "delivered to the push service" : d.error}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No devices with notifications on yet.</p>
              )}
              <p>Email: {result.email}</p>
              <p className="mt-1 text-ui-fg-subtle">Nothing popped up? Check the device isn't in Do Not Disturb / Focus mode and that notifications are allowed for your browser in the system settings.</p>
            </div>
          ) : null}
        </div>
      </Section>

      <Section title={`Your devices (${data.devices.length})`}>
        {data.devices.length ? (
          <ul className="divide-y divide-ui-border-base">
            {data.devices.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <Text size="small">{d.label ?? "Device"}</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">
                    Added {new Date(d.created_at).toLocaleDateString()}
                    {d.last_success_at ? ` · last notification ${new Date(d.last_success_at).toLocaleString()}` : ""}
                    {d.last_error ? ` · last error: ${d.last_error}` : ""}
                  </Text>
                </div>
                {d.failures ? <Badge size="2xsmall" color="orange">{d.failures} failed</Badge> : null}
                <Button size="small" variant="transparent" onClick={() => removeDevice(d.id)}>Remove</Button>
              </li>
            ))}
          </ul>
        ) : (
          <Text size="small" className="px-4 pb-4 text-ui-fg-subtle">No devices yet. Sign in on your phone and computer and choose Turn on when asked.</Text>
        )}
      </Section>

      <Section title="How you're told">
        <div className="grid grid-cols-1 gap-3 px-4 pb-4 sm:grid-cols-2">
          {(["high", "medium"] as const).map((pr) => (
            <div key={pr} className="rounded-lg border border-ui-border-base p-3">
              <Text size="small" weight="plus" className="capitalize">{pr} priority</Text>
              <label className="mt-2 flex items-center gap-2 text-[13px]">
                <Checkbox checked={p.push[pr]} onCheckedChange={(v) => set({ push: { ...p.push, [pr]: v === true } })} /> Push to my devices
              </label>
              <label className="mt-1 flex items-center gap-2 text-[13px]">
                <Checkbox checked={p.email[pr]} onCheckedChange={(v) => set({ email: { ...p.email, [pr]: v === true } })} /> Email me
              </label>
            </div>
          ))}
        </div>
        <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">Critical alerts always go by push, bell and email. Everything always lands in the bell. Problems from Issues ("stuck") are also emailed.</Text>
      </Section>

      {groups.map(({ pr, events }) => (
        <Section key={pr} title={PRIORITY[pr].title}>
          <Text size="xsmall" className="px-4 text-ui-fg-subtle">{PRIORITY[pr].hint}</Text>
          <ul className="grid grid-cols-1 gap-x-6 px-4 py-3 sm:grid-cols-2">
            {events.map((e) => {
              const on = pr === "critical" || !p.muted.includes(e.key)
              return (
                <li key={e.key}>
                  <label className="flex items-center gap-2 py-1 text-[13px]">
                    <Checkbox
                      checked={on}
                      disabled={pr === "critical"}
                      onCheckedChange={(v) => set({ muted: v === true ? p.muted.filter((m) => m !== e.key) : [...p.muted, e.key] })}
                    />
                    {e.label}
                  </label>
                </li>
              )
            })}
          </ul>
        </Section>
      ))}
    </div>
  )
}
